import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import {
  getPreferredSignInMethod,
  updatePreferredSignInMethod,
} from "../../../../utils/preferredSignInMethod/preferredSignInMethodApi";
import { useSnackAlert } from "../../../../contexts/SnackAlertContext";

// Keep in sync with accountSecurity.preferredSignInMethod in
// public/locales/en/common.json -- see the `translate` prop note in ../index.jsx.
const EN = {
  title: "Preferred sign in method",
  helper: "Choose which method to use by default when signing in.",
  passkey: "Passkey",
  app: "Authenticator App",
  updateSuccess: "Preferred sign-in method updated.",
  updateError: "Failed to update preferred sign-in method.",
  loadError: "Failed to load preferred sign-in method.",
};

// The whole section (title, divider, and the selector below) is only
// meaningful -- and only rendered -- once the user has both a passkey and
// the authenticator app configured. With just one (or neither) set up
// there's nothing to choose between: that method (or email OTP) already
// signs them in, decided automatically elsewhere
// (WebauthnCredentialsController#create, User#enable_authenticator_app!).
export default function PreferredSignInMethod({
  helperTextStyle,
  translate = true,
  containerSx,
  refreshToken,
}) {
  const { t: i18nT } = useTranslation("common", {
    keyPrefix: "accountSecurity.preferredSignInMethod",
  });
  const t = (key) => (translate ? i18nT(key) : EN[key]);
  const { setSnackAlert } = useSnackAlert();

  const [loading, setLoading] = useState(true);
  const [eligible, setEligible] = useState(false);
  const [preferredMethod, setPreferredMethod] = useState("passkey");
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const {
        preferred_2fa_method: method,
        passkey_configured: passkeyConfigured,
        app_configured: appConfigured,
      } = await getPreferredSignInMethod();

      setEligible(passkeyConfigured && appConfigured);
      if (method === "passkey" || method === "app") setPreferredMethod(method);
    } catch (e) {
      setSnackAlert(t("loadError"), "error");
    } finally {
      setLoading(false);
    }
  }, [setSnackAlert]);

  // refreshToken bumps whenever a sibling (Passkeys/AuthenticatorApp) changes
  // its own configured state -- see AccountSecurity/index.jsx.
  useEffect(() => {
    refresh();
  }, [refresh, refreshToken]);

  const handleChange = async (event) => {
    const method = event.target.value;
    const previousMethod = preferredMethod;
    setPreferredMethod(method);
    setSaving(true);
    try {
      await updatePreferredSignInMethod(method);
      setSnackAlert(t("updateSuccess"), "success");
    } catch (e) {
      setPreferredMethod(previousMethod);
      setSnackAlert(e.message || t("updateError"), "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !eligible) return null;

  return (
    <Box>
      <Divider sx={{ my: 2 }} />
      <Typography
        variant="h6"
        sx={{
          mt: 2,
          fontWeight: "normal",
        }}
      >
        {t("title")}
      </Typography>
      <Divider sx={{ my: 1 }} />
      <Box sx={containerSx}>
        <Typography variant="body2" sx={helperTextStyle}>
          {t("helper")}
        </Typography>
        <FormControl size="small" sx={{ mt: 1, minWidth: 220 }}>
          <Select
            value={preferredMethod}
            onChange={handleChange}
            disabled={saving}
          >
            <MenuItem value="passkey">{t("passkey")}</MenuItem>
            <MenuItem value="app">{t("app")}</MenuItem>
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
}
